import { CommonModule, Location } from '@angular/common';
import { Component, Input, OnInit, inject, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import {
  lucideTrendingUp,
  lucideTrendingDown,
  lucideBanknote,
  lucideClipboardList,
  lucideRefreshCw,
} from '@ng-icons/lucide';

import { ApiService } from '@mm-aggregator/drf-dynamics';
import type { EntityConfig } from '../../config/entity-config';
import { UI } from '../../components/ui';
import { ShellRoutesService } from '../../services/shell-routes.service';
import { goBackOrNavigate } from '../../utils/navigation';

interface LedgerLineItem {
  id: string;
  journal_entry_id: string;
  journal_ref: string;
  journal_event_type: string;
  journal_description: string;
  amount: string;
  side: 'DEBIT' | 'CREDIT';
  side_display: string;
  description: string;
  currency_code: string;
  created_at: string;
}

interface AccountHubData {
  account: Record<string, unknown>;
  balance: string;
  summary: {
    total_debits: string;
    total_credits: string;
    journal_entry_count: number;
    ledger_line_count: number;
    entries_by_type: { event_type: string; count: number }[];
  };
  recent_lines: LedgerLineItem[];
}

/**
 * Account detail hub page showing balance, transaction summary, and journal history.
 */
@Component({
  selector: 'app-account-detail',
  standalone: true,
  imports: [CommonModule, RouterLink, NgIcon, ...UI],
  templateUrl: './account-detail.component.html',
  viewProviders: [
    provideIcons({
      lucideTrendingUp,
      lucideTrendingDown,
      lucideBanknote,
      lucideClipboardList,
      lucideRefreshCw,
    }),
  ],
})
export class AccountDetailComponent implements OnInit {
  @Input() entityConfig!: EntityConfig;
  @Input() id!: string;

  private readonly api = inject(ApiService);
  private readonly location = inject(Location);
  private readonly router = inject(Router);
  readonly shellRoutes = inject(ShellRoutesService);

  readonly loading = signal(true);
  readonly error = signal<string | null>(null);
  readonly data = signal<AccountHubData | null>(null);

  ngOnInit(): void {
    this.loadHub();
  }

  goBack(): void {
    goBackOrNavigate(this.location, this.router, this.shellRoutes.commands('manage', this.entityConfig.key));
  }

  loadHub(): void {
    this.loading.set(true);
    this.error.set(null);
    this.api.get<AccountHubData>(`finances/accounts/${this.id}/hub/`).subscribe({
      next: (hub) => {
        this.data.set(hub);
        this.loading.set(false);
      },
      error: (err) => {
        this.error.set(err?.error?.detail ?? err?.message ?? 'Failed to load account data.');
        this.loading.set(false);
      },
    });
  }

  get account(): Record<string, unknown> | null {
    return this.data()?.account ?? null;
  }

  formatNumber(val: string | number): string {
    const n = typeof val === 'string' ? parseFloat(val) : val;
    if (isNaN(n)) return '0.00';
    return n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  }

  formatDate(iso: string): string {
    return new Date(iso).toLocaleString();
  }

  eventTypeClass(eventType: string): string {
    const map: Record<string, string> = {
      INITIATION: 'bg-info-bg text-info-fg',
      CONFIRMATION: 'bg-success-bg text-success-fg',
      REVERSAL: 'bg-danger-bg text-danger-fg',
      REFUND: 'bg-warning-bg text-warning-fg',
      ADJUSTMENT: 'bg-row-hover text-brand',
      SETTLEMENT: 'bg-success-bg text-success',
    };
    return map[eventType] ?? 'bg-surface-muted text-muted';
  }

  sideClass(side: string): string {
    return side === 'DEBIT' ? 'bg-danger-bg text-danger-fg' : 'bg-success-bg text-success-fg';
  }
}
